export type ApiErrorDetails = Record<string, unknown>

export class ApiRequestError extends Error {
  status: number
  code: string
  requestId?: string
  details?: ApiErrorDetails
  rawMessage: string

  constructor(params: {
    status: number
    code: string
    userMessage: string
    rawMessage: string
    requestId?: string
    details?: ApiErrorDetails
  }) {
    super(params.userMessage)
    this.name = 'ApiRequestError'
    this.status = params.status
    this.code = params.code
    this.requestId = params.requestId
    this.details = params.details
    this.rawMessage = params.rawMessage
  }
}

const STATUS_PREFIX_RE = /^\d{3}:\s*/
const CODE_TOKEN_RE = /^[a-z0-9_.:/-]+$/i
const CONTENT_POLICY_MESSAGE = 'Похоже, генерация случайно решила, что могут быть затронуты права на персонажа или бренд. Попробуйте изменить описание, убрать прямые упоминания или запустить генерацию в мягком режиме.'

const isContentPolicyText = (raw: string): boolean => {
  const normalized = String(raw || '').trim().toLowerCase()
  if (!normalized) return false
  return normalized.includes('unexpected status code: 422')
    || normalized.includes('http_422')
    || normalized.includes('status code: 422')
    || normalized.includes('status=422')
    || normalized.startsWith('422:')
    || normalized.includes('content policy')
    || normalized.includes('content policies')
    || normalized.includes('content_policy')
    || normalized.includes('policy violation')
    || normalized.includes('may violate')
    || normalized.includes('violates')
    || normalized.includes('moderation')
    || normalized.includes('safety policy')
    || normalized.includes('safety system')
    || normalized.includes('responsible ai')
    || normalized.includes('copyright')
    || normalized.includes('trademark')
}

const messageForCode = (codeRaw: string, status: number): string | null => {
  const code = String(codeRaw || '').trim().toLowerCase()
  if (!code) return null
  if (isContentPolicyText(code)) return CONTENT_POLICY_MESSAGE
  if (code === 'session_expired') return 'Сессия истекла. Войдите снова.'
  if (code === 'internal_error') return 'Внутренняя ошибка сервера. Попробуйте еще раз.'
  if (code === 'forbidden') return 'Недостаточно прав для выполнения действия.'
  if (code === 'not_found') return 'Запрошенные данные не найдены.'
  if (code === 'pipeline_not_found') return 'Инструмент не найден.'
  if (code === 'jobs_active_limit') return 'Слишком много активных запусков. Подождите немного и повторите.'
  if (code === 'payment_required' || code === 'not_enough_credits') return 'Недостаточно средств на балансе.'
  if (code === 'pack_disabled') return 'Этот тариф сейчас недоступен.'
  if (code === 'pack_not_found') return 'Тариф не найден.'
  if (code === 'promo_invalid_or_expired') return 'Промокод недействителен или истек.'
  if (code === 'receipt_email_invalid') return 'Введите корректный email для отправки чека.'
  if (
    code === 'payment_provider_disabled' ||
    code === 'payment_provider_unavailable' ||
    code === 'payment_provider_unsupported' ||
    code === 'yookassa_payment_disabled' ||
    code === 'robokassa_payment_disabled'
  ) return 'Оплата временно недоступна. Попробуйте позже или выберите другой способ оплаты.'
  if (code === 'pack_price_invalid') return 'У выбранного тарифа некорректная стоимость. Выберите другой тариф или напишите в поддержку.'
  if (code === 'project_folder_already_exists') return 'Папка с таким названием уже существует.'
  if (code === 'input_duplicate_ids') return 'Одно и то же изображение нельзя использовать дважды в одном запуске.'
  if (code === 'invalid_input_ids') return 'Некоторые входные изображения недоступны. Загрузите их заново.'
  if (code === 'no_inputs') return 'Добавьте входные изображения перед запуском.'
  if (code === 'asset_missing') return 'Файл загружен не полностью. Добавьте изображение заново.'
  if (code === 'pipeline_version_refresh_required') return 'Инструмент обновился. Обновите страницу и попробуйте снова.'
  if (code === 'source_input_required') return 'Выберите изображение-источник для авто-заполнения.'
  if (code === 'source_asset_not_found') return 'Исходное изображение недоступно. Загрузите его заново.'
  if (code === 'too_many_requests') return 'Слишком много запросов. Подождите немного и повторите.'
  if (code === 'service_unavailable') return 'Сервис временно недоступен. Попробуйте позже.'
  if (code === 'param_length_min:prompt') return 'Заполните поле длиннее.'
  if (code.startsWith('param_length_min:')) return 'Заполните поле длиннее и повторите.'
  if (code.startsWith('param_length_max:')) return 'Сократите текст и повторите.'
  if (code.endsWith('_required')) return 'Заполните обязательные данные и повторите.'
  if (code.endsWith('_not_found')) return 'Запрошенные данные не найдены.'
  if (code.endsWith('_invalid')) return 'Некорректные данные запроса.'
  if (
    code === 'submit_timeout' ||
    code.startsWith('submit_http_') ||
    code.startsWith('submit_error:') ||
    code.includes('provider_failed') ||
    code.includes('result_not_received')
  ) {
    return 'Серверы генерации сейчас работают нестабильно. Попробуйте через пару минут.'
  }
  if (code.startsWith('input_min_total:')) return 'Добавьте больше входных изображений перед запуском.'
  if (code.startsWith('input_max_total:')) return 'Превышен общий лимит входных изображений для этого инструмента.'
  if (code.startsWith('input_min_slot:') || code.startsWith('input_max_slot:')) return 'Проверьте количество файлов в слотах.'
  if (code.startsWith('input_mime_not_allowed:')) return 'Один из файлов имеет неподдерживаемый формат.'
  if (code.startsWith('http_5')) return 'Сервис временно недоступен. Попробуйте позже.'
  if (status === 401) return 'Сессия истекла. Войдите снова.'
  if (status === 403) return 'Недостаточно прав для выполнения действия.'
  if (status === 404) return 'Запрошенные данные не найдены.'
  if (status === 422) return '\u041f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u0432\u0432\u0435\u0434\u0435\u043d\u043d\u044b\u0435 \u0434\u0430\u043d\u043d\u044b\u0435 \u0438 \u043f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u0441\u043d\u043e\u0432\u0430.'
  if (status === 429) return 'Слишком много запросов. Подождите немного и повторите.'
  if (status >= 500) return 'Сервис временно недоступен. Попробуйте позже.'
  return null
}

const messageForKnownRawText = (raw: string): string | null => {
  const normalized = String(raw || '').trim().toLowerCase()
  if (!normalized) return null
  if (isContentPolicyText(normalized)) return CONTENT_POLICY_MESSAGE
  if (normalized.includes('token is not valid or has expired')) return 'Сессия истекла. Войдите снова.'
  if (normalized.includes('upload tokens invalid/expired')) return 'Срок загрузки файла истек. Загрузите файл заново.'
  if (normalized.includes('files[] required')) return 'Добавьте файлы для загрузки.'
  if (normalized.includes('only type=input supported')) return 'Сейчас поддерживается только загрузка входных файлов.'
  return null
}

const parseJson = (raw: string): Record<string, unknown> | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null
  } catch {
    return null
  }
}

const isCodeToken = (value: string) => {
  const token = String(value || '').trim()
  if (!token) return false
  if (!CODE_TOKEN_RE.test(token)) return false
  return token.includes('_') || token.includes(':') || token.includes('.')
}

const takeBefore = (value: string, separator: string) => {
  const index = value.indexOf(separator)
  return index >= 0 ? value.slice(0, index) : value
}

const normalizeCodeToken = (raw: string, fallback: string) => {
  const clean = String(raw || '').replace(STATUS_PREFIX_RE, '').trim()
  if (!clean) return fallback
  const candidate = ['\n', ' ', '{']
    .reduce((value, separator) => takeBefore(value, separator), clean)
    .trim()
  if (isCodeToken(candidate)) return candidate
  return fallback
}

const isReadableText = (raw: string) => {
  const clean = String(raw || '').replace(STATUS_PREFIX_RE, '').trim()
  if (!clean) return false
  return !isCodeToken(clean)
}

const mapValidationError = (entry: string): string | null => {
  const raw = String(entry || '').trim()
  if (!raw) return null
  const normalized = raw.replace(/^\[\d+\]\s*/g, '').trim()
  const lower = normalized.toLowerCase()
  if (lower.startsWith('dim_too_small')) return 'Изображение слишком маленькое. Минимум 256×256 px.'
  if (lower.startsWith('dim_too_large')) return 'Изображение слишком большое. Максимум 8192×8192 px.'
  if (lower.startsWith('mime_not_allowed')) return 'Неподдерживаемый формат файла. Разрешены JPG, PNG, WEBP.'
  if (lower.startsWith('size_too_large') || lower.startsWith('size_exceeded')) {
    const valueRaw = normalized.split(':').slice(1).join(':').trim()
    const bytes = Number(valueRaw)
    if (Number.isFinite(bytes) && bytes <= 0) return 'Файл загружен не полностью. Добавьте изображение заново.'
    return 'Файл слишком большой. Максимум 20 МБ.'
  }
  if (lower.startsWith('key_forbidden')) return 'Файл не прошел проверку доступа. Загрузите его заново.'
  return null
}

export const buildApiRequestError = (params: {
  status: number
  bodyText: string
  requestId?: string
}): ApiRequestError => {
  const status = Number(params.status) || 0
  const rawBody = String(params.bodyText || '').trim()
  const parsed = parseJson(rawBody)

  const payloadCode = typeof parsed?.code === 'string' ? parsed.code : null
  const payloadMessage = typeof parsed?.message === 'string' ? parsed.message : null
  const payloadRequestId = typeof parsed?.requestId === 'string' ? parsed.requestId : null
  const payloadDetails = parsed?.details && typeof parsed.details === 'object'
    ? parsed.details as ApiErrorDetails
    : undefined

  const payloadErrors = Array.isArray(parsed?.errors)
    ? parsed.errors.map((item) => String(item || '').trim()).filter(Boolean)
    : []
  const validationMessage = payloadErrors.length > 0 ? mapValidationError(payloadErrors[0]) : null

  const fallbackCode = `http_${status || 0}`
  const rawForCode = payloadCode || payloadMessage || rawBody
  const code = normalizeCodeToken(rawForCode, fallbackCode)

  const codeMessage = messageForCode(code, status)
  const rawMessage = payloadMessage || rawBody
  const knownRawMessage = messageForKnownRawText(rawMessage)
  const readableRaw = isReadableText(rawMessage) ? rawMessage.replace(STATUS_PREFIX_RE, '').trim() : null

  const userMessage =
    validationMessage ||
    codeMessage ||
    knownRawMessage ||
    readableRaw ||
    (status >= 500
      ? 'Сервис временно недоступен. Попробуйте позже.'
      : 'Не удалось выполнить запрос. Попробуйте еще раз.')

  return new ApiRequestError({
    status,
    code,
    userMessage,
    rawMessage: rawBody || code,
    requestId: params.requestId || payloadRequestId || undefined,
    details: payloadDetails
  })
}

export const getApiErrorCode = (error: unknown): string => {
  if (error && typeof error === 'object' && typeof (error as any).code === 'string') {
    return String((error as any).code)
  }
  const message = String((error as any)?.message || '')
  return normalizeCodeToken(message, '')
}
